import { Trash2 } from 'lucide-react'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Switch,
  Textarea,
} from '@/shared/ui/primitives'
import { DatePicker } from '@/shared/ui/components/date-picker'
import { FormErrorSummary } from '@/shared/ui/components/form-error-summary'
import { NumberInput } from '@/shared/ui/components/number-input'
import { scrollToFirstError } from '@/shared/infra/dom'
import type { Member, Todo, TodoCategory, TodoPriority, TodoVisibility } from '@/shared/domain'

export type TodoFormSubmit = {
  title: string
  description?: string
  priority: TodoPriority
  dueDate?: string
  categoryId?: string
  assignedTo?: string
  cadenceDays?: number
  equipmentItemId?: string
  visibility?: TodoVisibility
}

export type TodoEquipmentOption = {
  id: string
  name: string
}

export type TodoFormSheetProps = {
  mode: 'create' | 'edit'
  isOpen: boolean
  editingTodo: Todo | null
  categories: TodoCategory[]
  members: Member[]
  equipmentOptions: TodoEquipmentOption[]
  isSubmitting: boolean
  formError: string | null
  canDelete: boolean
  categorySlot?: ReactNode
  assigneeSlot?: ReactNode
  initialEquipmentItemId?: string
  initialCategoryId?: string
  lockEquipment?: boolean
  assigneeId?: string | null
  currentUserId?: string
  visibilityOrgName?: string
  onClose: () => void
  onSubmit: (params: TodoFormSubmit) => void
  onDelete: () => void
}

const NO_EQUIPMENT_VALUE = '__none__'

const QUICK_TEMPLATES = [
  'Blind repair',
  'Decoy maintenance',
  'Water level check',
  'Guest waivers',
  'Equipment oil change',
  'Trail cam check',
  'Food plot flood',
  'License renewal',
  'Scouting',
  'Cleanup',
] as const

const PRIORITY_OPTIONS: TodoPriority[] = ['low', 'normal', 'high']

const PRIORITY_LABELS: Record<TodoPriority, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
}

function defaultVisibility(assigneeId: string | null | undefined, currentUserId: string | undefined): TodoVisibility {
  const assignee = assigneeId ?? currentUserId ?? ''
  return assignee !== (currentUserId ?? '') ? 'public' : 'private'
}

function formatDueDateForInput(dueDate: string | undefined): string {
  if (!dueDate) return ''
  const parsed = new Date(dueDate)
  if (Number.isNaN(parsed.getTime())) return ''
  const year = parsed.getUTCFullYear()
  const month = String(parsed.getUTCMonth() + 1).padStart(2, '0')
  const day = String(parsed.getUTCDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function TodoFormSheet({
  mode,
  isOpen,
  editingTodo,
  equipmentOptions,
  isSubmitting,
  formError,
  canDelete,
  categorySlot,
  assigneeSlot,
  initialEquipmentItemId,
  initialCategoryId,
  lockEquipment,
  assigneeId,
  currentUserId,
  visibilityOrgName,
  onClose,
  onSubmit,
  onDelete,
}: TodoFormSheetProps) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState<TodoPriority>('normal')
  const [dueDate, setDueDate] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [assignedTo, setAssignedTo] = useState('')
  const [cadenceDays, setCadenceDays] = useState<number | undefined>(undefined)
  const [equipmentItemId, setEquipmentItemId] = useState<string>('')
  const [visibilityChoice, setVisibilityChoice] = useState<TodoVisibility | null>(null)
  const [localTitleError, setLocalTitleError] = useState<string | null>(null)
  const titleRef = useRef<HTMLInputElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  const initialCategoryIdRef = useRef(initialCategoryId)
  const initialEquipmentItemIdRef = useRef(initialEquipmentItemId)
  initialCategoryIdRef.current = initialCategoryId
  initialEquipmentItemIdRef.current = initialEquipmentItemId

  useEffect(() => {
    if (!isOpen) return
    if (mode === 'edit' && editingTodo) {
      setTitle(editingTodo.title)
      setDescription(editingTodo.description ?? '')
      setPriority(editingTodo.priority)
      setDueDate(formatDueDateForInput(editingTodo.dueDate))
      setCategoryId(editingTodo.categoryId ?? '')
      setAssignedTo(editingTodo.assignedTo ?? '')
      setCadenceDays(editingTodo.cadenceDays)
      setEquipmentItemId(editingTodo.equipmentItemId ?? '')
      setVisibilityChoice(editingTodo.visibility ?? 'private')
    } else {
      setTitle('')
      setDescription('')
      setPriority('normal')
      setDueDate('')
      setCategoryId(initialCategoryIdRef.current ?? '')
      setAssignedTo('')
      setCadenceDays(undefined)
      setEquipmentItemId(initialEquipmentItemIdRef.current ?? '')
      setVisibilityChoice(null)
    }
    setLocalTitleError(null)
  }, [isOpen, mode, editingTodo])

  useEffect(() => {
    if (isOpen) {
      titleRef.current?.focus()
    }
  }, [isOpen])

  const visibility = visibilityChoice ?? defaultVisibility(assigneeId, currentUserId)

  const handleSubmit = () => {
    if (!title.trim()) {
      setLocalTitleError('Title is required')
      scrollToFirstError(bodyRef.current)
      return
    }
    setLocalTitleError(null)
    onSubmit({
      title: title.trim(),
      description: description.trim() || undefined,
      priority,
      dueDate: dueDate || undefined,
      categoryId: categoryId || undefined,
      assignedTo: assignedTo || undefined,
      cadenceDays,
      equipmentItemId: equipmentItemId || undefined,
      visibility,
    })
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) onClose()
  }

  const titleLabel = mode === 'create' ? 'Add To-Do' : 'Edit To-Do'
  const submitLabel = isSubmitting
    ? 'Saving…'
    : 'Save'

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{titleLabel}</SheetTitle>
          <SheetDescription>Assign work to keep your operation running</SheetDescription>
        </SheetHeader>

        <div ref={bodyRef} className="flex flex-col gap-4 px-4 pb-4">
          {mode === 'create' ? (
            <div className="flex flex-wrap gap-2">
              {QUICK_TEMPLATES.map((template) => (
                <Button
                  key={template}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setTitle(template)}
                >
                  {template}
                </Button>
              ))}
            </div>
          ) : null}

          <div className="flex flex-col gap-2">
            <Label htmlFor="todo-form-title">Title</Label>
            <Input
              id="todo-form-title"
              ref={titleRef}
              value={title}
              onChange={(event) => {
                const next = event.target.value
                setTitle(next)
                if (localTitleError && next.trim()) {
                  setLocalTitleError(null)
                }
              }}
              maxLength={200}
              aria-invalid={localTitleError ? true : undefined}
            />
            {localTitleError ? (
              <p role="alert" className="text-sm text-destructive">
                {localTitleError}
              </p>
            ) : null}
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="todo-form-description">Description</Label>
            <Textarea
              id="todo-form-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={3}
              maxLength={2000}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label>Priority</Label>
            <div className="flex gap-2">
              {PRIORITY_OPTIONS.map((option) => (
                <Button
                  key={option}
                  type="button"
                  variant={priority === option ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPriority(option)}
                  aria-pressed={priority === option}
                >
                  {PRIORITY_LABELS[option]}
                </Button>
              ))}
            </div>
          </div>

          <DatePicker
            id="todo-form-due-date"
            label="Due date"
            value={dueDate}
            clearable
            onChange={setDueDate}
          />

          <div className="flex flex-col gap-2">
            <Label htmlFor="todo-form-cadence">Repeat every (days)</Label>
            <NumberInput
              id="todo-form-cadence"
              mode="integer"
              value={cadenceDays?.toString() ?? ''}
              onChange={(next) => {
                if (next === '') {
                  setCadenceDays(undefined)
                  return
                }
                const parsed = Number.parseInt(next, 10)
                setCadenceDays(Number.isNaN(parsed) ? undefined : parsed)
              }}
              placeholder="Leave blank for one-time"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="todo-form-equipment">Equipment item</Label>
            <Select
              value={equipmentItemId === '' ? NO_EQUIPMENT_VALUE : equipmentItemId}
              onValueChange={(value) =>
                setEquipmentItemId(value === NO_EQUIPMENT_VALUE ? '' : value)
              }
              disabled={lockEquipment}
            >
              <SelectTrigger id="todo-form-equipment">
                <SelectValue placeholder="Not linked" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_EQUIPMENT_VALUE}>Not linked</SelectItem>
                {equipmentOptions.map((option) => (
                  <SelectItem key={option.id} value={option.id}>
                    {option.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>{categorySlot}</div>

          <div>{assigneeSlot}</div>

          <div className="flex items-center gap-3 rounded-md border px-4 py-3">
            <Switch
              id="todo-form-visibility"
              checked={visibility === 'public'}
              onCheckedChange={(checked) => setVisibilityChoice(checked ? 'public' : 'private')}
              aria-label="Public"
            />
            <div className="min-w-0 flex-1">
              <Label htmlFor="todo-form-visibility">Public</Label>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {visibility === 'public'
                  ? `Everyone in ${visibilityOrgName || 'your organization'} can see and complete this`
                  : 'Only you, the assignee and managers can see this'}
              </p>
            </div>
          </div>

          {formError ? (
            <p role="alert" className="text-sm text-destructive">
              {formError}
            </p>
          ) : null}
        </div>

        <SheetFooter className="flex-row items-center gap-2">
          <div>
            {mode === 'edit' && canDelete ? (
              <Button
                type="button"
                variant="outline"
                className="text-destructive"
                onClick={onDelete}
                aria-label="Delete to-do"
              >
                <Trash2 className="size-4" />
              </Button>
            ) : null}
          </div>
          <FormErrorSummary count={localTitleError ? 1 : 0} />
          <div className="ml-auto flex items-center gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={isSubmitting}
              onClick={handleSubmit}
            >
              {submitLabel}
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
